import React from 'react';
import Icon from '../../../components/AppIcon';

const LocationInfo = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Location Details */}
          <div>
            <div className="mb-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
                Based in Sri Lanka,
                <span className="gradient-text block">Working Globally</span>
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed">
                Located in the beautiful island nation of Sri Lanka, I collaborate with clients 
                and teams worldwide. My strategic location allows me to work effectively with 
                both Asian and Western markets.
              </p>
            </div>

            {/* Location Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start space-x-4">
                <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg flex-shrink-0">
                  <Icon name="MapPin" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary mb-1">Location</h3>
                  <p className="text-text-secondary text-sm">Colombo, Sri Lanka</p>
                  <p className="text-text-muted text-xs">GMT +5:30 (IST)</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-lg flex-shrink-0">
                  <Icon name="Wifi" size={24} className="text-success" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary mb-1">Connectivity</h3>
                  <p className="text-text-secondary text-sm">High-speed Internet</p>
                  <p className="text-text-muted text-xs">Fiber optic connection</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-lg flex-shrink-0">
                  <Icon name="Globe" size={24} className="text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary mb-1">Remote Work</h3>
                  <p className="text-text-secondary text-sm">4+ years experience</p>
                  <p className="text-text-muted text-xs">Global collaboration</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg flex-shrink-0">
                  <Icon name="Languages" size={24} className="text-warning" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary mb-1">Languages</h3>
                  <p className="text-text-secondary text-sm">English (Fluent)</p>
                  <p className="text-text-muted text-xs">Sinhala (Native)</p>
                </div>
              </div>
            </div>

            {/* Working Hours */}
            <div className="bg-surface rounded-xl p-6">
              <h3 className="font-semibold text-text-primary mb-4 flex items-center">
                <Icon name="Clock" size={20} className="mr-2 text-primary" />
                Working Hours & Availability
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">Local Time (IST)</span>
                  <span className="font-medium text-text-primary">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">US Eastern Time</span>
                  <span className="font-medium text-text-primary">10:30 PM - 7:30 AM</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">UK Time</span>
                  <span className="font-medium text-text-primary">3:30 AM - 12:30 PM</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">Australia (AEST)</span>
                  <span className="font-medium text-text-primary">1:30 PM - 10:30 PM</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-accent/10 rounded-lg border border-accent/20">
                <p className="text-xs text-accent">
                  <Icon name="Info" size={14} className="inline mr-1" />
                  Flexible with meeting times for international clients. Available for urgent matters via WhatsApp.
                </p>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="relative">
            <div className="bg-surface rounded-2xl p-6 shadow-lg">
              <div className="aspect-video rounded-xl overflow-hidden bg-muted">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="Colombo, Sri Lanka"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps?q=6.9271,79.8612&z=12&output=embed"
                  className="border-0"
                ></iframe>
              </div>
              
              {/* Map Info */}
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-text-primary">Colombo, Sri Lanka</h4>
                    <p className="text-sm text-text-secondary">Commercial Capital</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-text-primary">GMT +5:30</p>
                    <p className="text-xs text-text-muted">Indian Standard Time</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                  <div className="text-center">
                    <Icon name="Plane" size={20} className="text-accent mx-auto mb-2" />
                    <p className="text-xs text-text-muted">International Airport</p>
                    <p className="text-sm font-medium text-text-primary">45 min away</p>
                  </div>
                  <div className="text-center">
                    <Icon name="Building" size={20} className="text-primary mx-auto mb-2" />
                    <p className="text-xs text-text-muted">Tech Hub</p>
                    <p className="text-sm font-medium text-text-primary">City Center</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Stats */}
            <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4 border border-border">
              <div className="text-center">
                <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-2">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                </div>
                <p className="text-xs font-medium text-text-primary">Online Now</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationInfo;